import { ArrowDown, ArrowUp } from "lucide-react";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import styles from './DropdownSelect.module.css';

function normaliseOption(option) {
  if (option && typeof option === 'object') {
    const value = option.value ?? option.id ?? option.label;
    return {
      value,
      label: String(option.label ?? value),
    };
  }

  return { value: option, label: String(option) };
}

function DropdownSelect({
  label,
  value,
  onChange,
  options = [],
  disabled = false,
  border = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const selectRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);

  const close = useCallback(() => {
    setIsOpen(false);
    setFocused(false);
    setHighlightIndex(-1);
    buttonRef.current?.focus();
  }, []);

  const hasValue = value !== '' && value !== null && value !== undefined;
  const isFloating = focused || hasValue || isOpen;

  const allOptions = useMemo(
    () => [{ value: '', label: 'All' }, ...options.map(normaliseOption)],
    [options]
  );

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        close();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, close]);

  const handleSelect = useCallback((optionValue) => {
    onChange({ target: { value: optionValue } });
    close();
  }, [onChange, close]);

  const toggleOpen = useCallback(() => {
    if (disabled) return;
    if (isOpen) {
      close();
    } else {
      setIsOpen(true);
      setFocused(true);
      const idx = allOptions.findIndex(o => o.value === value);
      setHighlightIndex(idx >= 0 ? idx : 0);
    }
  }, [disabled, isOpen, close, value, allOptions]);

  const handleTriggerKeyDown = useCallback((e) => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        toggleOpen();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocused(true);
          setHighlightIndex(0);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocused(true);
          setHighlightIndex(allOptions.length - 1);
        }
        break;
      case 'Escape':
        if (isOpen) {
          e.preventDefault();
          close();
        }
        break;
    }
  }, [disabled, isOpen, toggleOpen, close, allOptions.length]);

  const handleListKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIndex(prev => (prev + 1) % allOptions.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIndex(prev => (prev - 1 + allOptions.length) % allOptions.length);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (highlightIndex >= 0 && highlightIndex < allOptions.length) {
          handleSelect(allOptions[highlightIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'Home':
        e.preventDefault();
        setHighlightIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setHighlightIndex(allOptions.length - 1);
        break;
    }
  }, [allOptions, highlightIndex, handleSelect, close]);

  useEffect(() => {
    if (!isOpen || highlightIndex < 0) return;
    const list = listRef.current;
    if (!list) return;
    const item = list.children[highlightIndex];
    item?.scrollIntoView({ block: "nearest" });
  }, [isOpen, highlightIndex])

  const selectedOption = allOptions.find((option) => option.value === value);
  const displayValue = hasValue ? selectedOption?.label ?? String(value) : '';

  return (
    <div className={[
      styles.container,
      border && styles.border
    ].filter(Boolean).join(" ")}
      ref={selectRef}
    >
      <div className={styles.selector}>
        <label className={[
          styles.label,
          isFloating && styles.labelFloating,
          focused && styles.labelFocused
        ].filter(Boolean).join(" ")}
        >
          {label}
        </label>
        <button
          ref={buttonRef}
          type="button"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={isOpen ? `${label}-listbox` : undefined}
          aria-activedescendant={
            isOpen && highlightIndex >= 0
              ? `${label}-option-${highlightIndex}`
              : undefined
          }
          disabled={disabled}
          className={[
            styles.input,
            focused && styles.inputFocused,
            hasValue && styles.inputFocused,
            disabled && styles.inputDisabled
          ].filter(Boolean).join(" ")}
          onClick={toggleOpen}
          onFocusCapture={() => setFocused(true)}
          onKeyDown={handleTriggerKeyDown}
        >
          <span className={!hasValue ? styles.placeholder : ''}>
            {displayValue || ' '}
          </span>
        </button>

        {isOpen && (
          <div
            ref={listRef}
            id={`${label}-listbox`}
            className={styles.dropdown}
            role="listbox"
            aria-label={label}
            tabIndex={-1}
            onKeyDown={handleListKeyDown}
          >
            {allOptions.map((opt, index) => (
              <div
                key={`${String(opt.value)}-${index}`}
                id={`${label}-option-${index}`}
                role="option"
                aria-selected={value === opt.value}
                className={[
                  styles.option,
                  value === opt.value && styles.selected,
                  index === highlightIndex && styles.highlighted
                ].filter(Boolean).join(" ")}
                onClick={() => handleSelect(opt.value)}
                onMouseEnter={() => setHighlightIndex(index)}>
                {opt.label}
              </div>
            ))}
            {/* <div className={styles.option}
              onClick={() => handleSelect('')}
            >
              <em style={{ marginRight: "auto" }}>All</em>
            </div>
            {options.map((opt) => (
              <div
                key={String(opt)}
                role="option"
                className={[
                  styles.option,
                  value === opt && styles.selected,
                ].filter(Boolean).join(" ")}
                onClick={() => handleSelect(opt)}
              >
                {String(opt)}
              </div>
            ))} */}
          </div>
        )}

        {isOpen ? <ArrowDown className={styles.icon} /> : <ArrowUp className={styles.icon} />}
      </div>
    </div>
  );
}

export default DropdownSelect;
